import { DynamicTypes, IComponent, IDynamicTypeInfo } from "@kuika/kuika-cl-model";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

interface IDefaultStylesResult {
  componentName: string;
  mode: string;
  hasDefaultStyles: boolean;
  defaultStyles: Record<string, unknown>;
  styleRestrictions: {
    inVisibleStyles: string[];
    blockStyle: boolean;
  };
}

export function getDesignerDefaultStyles(
  sdInstance: ScreenDesignInstance,
  input: { componentName?: string; isMobile?: boolean }
): IDefaultStylesResult | { error: string } {
  const { componentName, isMobile } = input;
  if (!componentName) return { error: "componentName is required" };

  const sd = getValues(sdInstance);
  const defaultStyleProvider = sd?.defaultStyleProvider;

  if (!defaultStyleProvider) {
    return { error: "Default style provider is not available" };
  }

  let defaultStyles: Record<string, unknown>;
  try {
    defaultStyles = defaultStyleProvider.getDefaultStyles(componentName, !!isMobile);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    return { error: `Failed to get default styles: ${message}` };
  }

  const componentDefs = sd.project?.componentSetObj?.components;
  let inVisibleStyles: string[] = [];
  let blockStyle = false;

  if (componentDefs) {
    const componentDef = componentDefs.find((component: IComponent) => component.name === componentName);
    if (componentDef) {
      inVisibleStyles = componentDef.inVisibleStyles || [];
      blockStyle = !!componentDef.blockStyle;
    }
  }

  const hasStyles = defaultStyles && Object.keys(defaultStyles).length > 0;

  return {
    componentName,
    mode: isMobile ? "mobile" : "desktop",
    hasDefaultStyles: hasStyles,
    defaultStyles: hasStyles ? defaultStyles : {},
    styleRestrictions: {
      inVisibleStyles,
      blockStyle
    }
  };
}

interface IDynamicTypeEntry {
  key: string;
  returnTypeName: string | null;
  returnCardinality: string | null;
  path: string | null;
}

interface IDynamicTypesResult {
  bags: {
    count: number;
    items: IDynamicTypeEntry[];
  };
  caches: {
    count: number;
    items: IDynamicTypeEntry[];
  };
  totalCount: number;
}

export async function getDesignerDynamicTypes(
  sdInstance: ScreenDesignInstance
): Promise<IDynamicTypesResult | { error: string }> {
  const sd = getValues(sdInstance);

  if (!sd.fetchDynamicTypeKeys) {
    return { error: "Dynamic types provider is not available" };
  }

  const result = await sd.fetchDynamicTypeKeys();
  const bags: DynamicTypes = result.bags || {};
  const caches: DynamicTypes = result.caches || {};

  const bagKeys = Object.keys(bags);
  const cacheKeys = Object.keys(caches);

  const formatEntry = (key: string, info: IDynamicTypeInfo): IDynamicTypeEntry => ({
    key,
    returnTypeName: info.returnTypeName || null,
    returnCardinality: info.returnCardinality != null ? String(info.returnCardinality) : null,
    path: info.path || null
  });

  return {
    bags: {
      count: bagKeys.length,
      items: bagKeys.map((key) => formatEntry(key, bags[key]))
    },
    caches: {
      count: cacheKeys.length,
      items: cacheKeys.map((key) => formatEntry(key, caches[key]))
    },
    totalCount: bagKeys.length + cacheKeys.length
  };
}
